import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../../../../foundation/altzero_base/base/components/ui/card';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import type { Health, JobHealth } from '../../lib/jobHealth';
import { timeAgo } from '../../lib/timeAgo';

const DOT: Record<Health, string> = {
  green: 'bg-green-500', amber: 'bg-amber-500', red: 'bg-red-500', paused: 'bg-gray-400', off: 'bg-gray-400',
};

export const HealthDot = ({ health }: { health: Health }) => (
  <span role="img" aria-label={health} title={health} className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-full', DOT[health])} />
);

/** A job's own line: dot, label, "last ran X ago (automatic/manual)", last error, Recent runs toggle. */
/** Spec §6/§7: a run is automatic or manual; the raw source (pg_cron, worker, cloud_run) is not shown. */
const sourceLabel = (source: string): string => (source === 'manual' ? 'manual' : 'automatic');

export const JobRow = ({ job, showDot = true }: { job: JobHealth | undefined; showDot?: boolean }) => {
  const [open, setOpen] = useState(false);
  if (!job) return <p className="text-sm text-gray-500">No data</p>;
  return (
    <div className="min-w-0 space-y-1" data-testid={`job-row-${job.job}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        {showDot && <HealthDot health={job.health} />}
        <span className="font-medium text-gray-800">{job.label}</span>
        <span className="text-xs text-gray-500">
          last ran {timeAgo(job.lastRunAt)}{job.lastSource ? ` (${sourceLabel(job.lastSource)})` : ''}
        </span>
      </div>
      {job.lastError && <p className="break-words text-xs text-red-700">{job.lastError}</p>}
      {job.recent.length > 0 && (
        <>
          <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
            className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} Recent runs
          </button>
          {open && (
            <ul className="space-y-0.5 text-xs text-gray-600">
              {job.recent.map((r, i) => (
                <li key={`${r.startedAt}-${i}`} className="min-w-0 break-words">
                  {new Date(r.startedAt).toLocaleString('en-AU')} · {sourceLabel(r.source)} · {r.status}{r.error ? ` · ${r.error}` : ''}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

type JobCardProps = {
  testId: string;
  wide?: boolean;
  title: string;
  health: Health;
  /** The job(s) this card reports; one JobRow each. */
  jobs?: Array<JobHealth | undefined>;
  link?: { to: string; label: string };
  children?: ReactNode;
};

export const JobCard = ({ testId, wide, title, health, jobs = [], link, children }: JobCardProps) => (
  <Card data-testid={testId} className={cn('min-w-0 border-0 shadow-sm', wide && 'lg:col-span-2')}>
    <CardContent className="space-y-3 pt-4 pb-4">
      <div className="flex items-center gap-2">
        <HealthDot health={health} />
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {link && <Link to={link.to} className="ml-auto text-xs text-blue-700 hover:underline">{link.label}</Link>}
      </div>
      {jobs.map((j, i) => <JobRow key={j?.job ?? i} job={j} showDot={jobs.length > 1} />)}
      {children && <div className="min-w-0 space-y-2 text-xs text-gray-700">{children}</div>}
    </CardContent>
  </Card>
);
